import React, { useState } from 'react';
import { importEAPFile, validateEAPFile, ImportResult } from '../services/importService';

interface EAPImportDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onImportSuccess: () => void;
}

const EAPImportDialog: React.FC<EAPImportDialogProps> = ({ isOpen, onClose, onImportSuccess }) => {
  const [file, setFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setError(null);
      setResult(null);
    }
  };

  const handleImport = async () => {
    if (!file) {
      setError('Пожалуйста, выберите файл для импорта');
      return;
    }

    // Validate file
    const validation = validateEAPFile(file);
    if (!validation.isValid) {
      setError(validation.error || 'Ошибка валидации файла');
      return;
    }

    setIsLoading(true);
    setError(null);
    setResult(null);

    try {
      const result = await importEAPFile(file);
      setResult(result);
      onImportSuccess();
    } catch (err: any) {
      console.error('Import error:', err);
      setError(err.response?.data?.detail || err.message || 'Ошибка при импорте файла');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setResult(null);
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        padding: '24px',
        borderRadius: '8px',
        maxWidth: '800px',
        maxHeight: '90vh',
        overflow: 'auto',
        width: '100%'
      }}>
        <h2 style={{ marginTop: 0, marginBottom: '20px' }}>
          Импорт ЕАП данных
        </h2>

        {!result ? (
          <div>
            <div style={{ marginBottom: '20px' }}>
              <p style={{ marginBottom: '10px' }}>
                Выберите файл ЕАП для импорта (Excel или CSV):
              </p>
              <input
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleFileChange}
                style={{ marginBottom: '10px' }}
              />
              {file && (
                <p style={{ color: 'green', marginBottom: '10px' }}>
                  Выбран файл: {file.name}
                </p>
              )}
            </div>

            {error && (
              <div style={{ 
                backgroundColor: '#ffebee', 
                color: '#c62828', 
                padding: '10px', 
                borderRadius: '4px',
                marginBottom: '20px'
              }}>
                {error}
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button 
                onClick={handleClose}
                disabled={isLoading}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  backgroundColor: 'white',
                  cursor: 'pointer'
                }}
              >
                Отмена
              </button>
              <button 
                onClick={handleImport}
                disabled={!file || isLoading}
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  borderRadius: '4px',
                  backgroundColor: '#1976d2',
                  color: 'white',
                  cursor: !file || isLoading ? 'not-allowed' : 'pointer',
                  opacity: !file || isLoading ? 0.6 : 1
                }}
              >
                {isLoading ? 'Импорт...' : 'Импортировать'}
              </button>
            </div>
          </div>
        ) : (
          <div>
            <div style={{ 
              backgroundColor: '#e8f5e8', 
              color: '#2e7d32', 
              padding: '15px', 
              borderRadius: '4px',
              marginBottom: '20px'
            }}>
              <h3 style={{ marginTop: 0 }}>✅ Импорт успешно завершен!</h3>
              <p><strong>Файл:</strong> {result.filename}</p>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <h4>📊 Сводка импорта:</h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px' }}>
                <div style={{ padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                  <strong>Партнеры:</strong> {result.summary.partners}
                </div>
                <div style={{ padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                  <strong>Работники:</strong> {result.summary.workers}
                </div>
                <div style={{ padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                  <strong>Магазины:</strong> {result.summary.stores}
                </div>
                <div style={{ padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                  <strong>Программы:</strong> {result.summary.programs}
                </div>
                <div style={{ padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                  <strong>Связи:</strong> {result.summary.program_stores}
                </div>
              </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <h4>🏪 Магазины с геокодированием:</h4>
              <div style={{ maxHeight: '200px', overflow: 'auto', border: '1px solid #ddd', borderRadius: '4px' }}>
                {result.imported_data.stores.map((store, index) => (
                  <div key={store.id} style={{ 
                    padding: '8px', 
                    borderBottom: index < result.imported_data.stores.length - 1 ? '1px solid #eee' : 'none',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div>
                      <strong>{store.address}</strong>
                      <br />
                      <small>Сеть: {store.chain}</small>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <span style={{ 
                        color: store.geocoded ? '#2e7d32' : '#f57c00',
                        fontWeight: 'bold'
                      }}>
                        {store.geocoded ? '✅ Геокодирован' : '❌ Не геокодирован'}
                      </span>
                      <br />
                      <small style={{ color: '#666' }}>
                        {store.action === 'created' ? 'Создан' : 'Обновлен'}
                      </small>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <h4>👥 Работники с геокодированием:</h4>
              <div style={{ maxHeight: '200px', overflow: 'auto', border: '1px solid #ddd', borderRadius: '4px' }}>
                {result.imported_data.workers.map((worker, index) => (
                  <div key={worker.id} style={{ 
                    padding: '8px', 
                    borderBottom: index < result.imported_data.workers.length - 1 ? '1px solid #eee' : 'none',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div>
                      <strong>{worker.name}</strong>
                      <br />
                      <small>Тип: {worker.type === 'merchandiser' ? 'Мерчендайзер' : 'Супервайзер'}</small>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <span style={{ 
                        color: worker.geocoded ? '#2e7d32' : '#f57c00',
                        fontWeight: 'bold'
                      }}>
                        {worker.geocoded ? '✅ Геокодирован' : '❌ Не геокодирован'}
                      </span>
                      <br />
                      <small style={{ color: '#666' }}>
                        {worker.action === 'created' ? 'Создан' : 'Обновлен'}
                      </small>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button 
                onClick={handleClose}
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  borderRadius: '4px',
                  backgroundColor: '#1976d2',
                  color: 'white',
                  cursor: 'pointer'
                }}
              >
                Закрыть
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EAPImportDialog; 